import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadAttachment } from '../../../../lib/attachments';
import type { PendingAttachment } from '../../../../lib/use-chat';
import type { useGenericBi } from '../../../../lib/use-generic-bi';
import { CHAT_HOME_BI } from '../message-utils';

type LogPageClick = ReturnType<typeof useGenericBi>['logPageClickBi'];

export function useAttachments({
  logPageClickBi,
  pendingUploadFiles,
  clearUpload,
}: {
  logPageClickBi: LogPageClick;
  pendingUploadFiles: File[] | null;
  clearUpload: () => void;
}) {
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [dragFileCount, setDragFileCount] = useState(0);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = useCallback(() => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: 'attach_file', location: 'composer' });
    fileInputRef.current?.click();
  }, []);

  const startUploads = useCallback((files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const newPending: PendingAttachment[] = arr.map((file) => ({
      id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      status: 'uploading',
    }));
    setPendingAttachments((prev) => [...prev, ...newPending]);
    for (const p of newPending) {
      void uploadAttachment(p.file).then((result) => {
        if (!result.ok) {
          logPageClickBi({
            page_name: 'chat',
            button_name: 'attachment_upload_failed',
            state: p.file.type,
            action: result.message.slice(0, 200),
          });
        }
        setPendingAttachments((prev) =>
          prev.map((q) =>
            q.id === p.id
              ? result.ok
                ? { ...q, status: 'done', attachment: result.attachment }
                : { ...q, status: 'error', error: result.message, errorKind: result.kind }
              : q,
          ),
        );
      });
    }
  }, []);

  // Paste-to-attach: a screenshot / copied image lands as an attachment, same as
  // the file dialog. A plain <textarea> ignores image clipboard data, so without
  // this the paste silently does nothing. Text paste falls through untouched.
  const onComposerPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length === 0) return;
      e.preventDefault();
      startUploads(files);
    },
    [startUploads],
  );

  // Files the home launcher handed off — upload on mount so the attachment is
  // ready in the composer. The ref makes consumption idempotent against
  // StrictMode's double-run (clearUpload's state update lands after the 2nd run).
  const consumedUploadsRef = useRef<File[] | null>(null);
  useEffect(() => {
    if (!pendingUploadFiles || pendingUploadFiles.length === 0) return;
    if (consumedUploadsRef.current === pendingUploadFiles) return;
    consumedUploadsRef.current = pendingUploadFiles;
    startUploads(pendingUploadFiles);
    clearUpload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUploadFiles]);

  const removeAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const clearAllAttachments = useCallback(() => {
    setPendingAttachments((prev) => {
      prev.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
      return [];
    });
  }, []);

  const retryAttachment = useCallback(
    (id: string) => {
      setPendingAttachments((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: 'uploading', error: undefined, errorKind: undefined } : p)),
      );
      const target = pendingAttachments.find((p) => p.id === id);
      if (!target) return;
      void uploadAttachment(target.file).then((result) => {
        setPendingAttachments((prev) =>
          prev.map((q) =>
            q.id === id
              ? result.ok
                ? { ...q, status: 'done', attachment: result.attachment }
                : { ...q, status: 'error', error: result.message, errorKind: result.kind }
              : q,
          ),
        );
      });
    },
    [pendingAttachments],
  );

  // Free any blob URLs the chip thumbnails held when the panel unmounts.
  useEffect(() => {
    return () => {
      pendingAttachments.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) {
      setDragActive(true);
      const count = Array.from(e.dataTransfer.items).filter((i) => i.kind === 'file').length;
      setDragFileCount(count);
    }
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setDragActive(false);
      setDragFileCount(0);
    }
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
  };
  const onDrop = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragActive(false);
    setDragFileCount(0);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      startUploads(e.dataTransfer.files);
    }
  };

  const uploadsInFlight = pendingAttachments.some((p) => p.status === 'uploading');
  const readyAttachments = pendingAttachments.filter((p) => p.status === 'done' && p.attachment);

  return {
    fileInputRef,
    pendingAttachments,
    setPendingAttachments,
    dragActive,
    dragFileCount,
    openFilePicker,
    startUploads,
    onComposerPaste,
    removeAttachment,
    clearAllAttachments,
    retryAttachment,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    uploadsInFlight,
    readyAttachments,
  };
}
